import { flagEmojiOf, isCountryCode } from '@aysar/contracts';
import { Globe, X } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { MultiSelect } from '../MultiSelect/MultiSelect';
import {
  COUNTRY_SET_ANY_CLASSES,
  COUNTRY_SET_CHIP_REMOVE_CLASSES,
  SELECTION_CHIP_CLASSES,
  SELECTION_CHIP_RAIL_CLASSES,
} from '../selection.tokens';
import { type SelectionOption } from '../types/selection-option.types';

import { type CountrySetFieldProps } from './CountrySetField.types';
import { useCountrySetFieldLogic } from './useCountrySetFieldLogic';

/** Module-scope so the panel's filter memo is not re-run on every render (see `MultiSelectProps.filterFn`). */
function renderCountryOption(option: SelectionOption) {
  return (
    <span className="flex min-w-0 items-center gap-sm">
      {isCountryCode(option.value) ? (
        <span aria-hidden className="shrink-0">
          {flagEmojiOf(option.value)}
        </span>
      ) : null}
      <span className="truncate">{option.label}</span>
      <span className="ms-auto shrink-0 text-caption text-muted-foreground">{option.value}</span>
    </span>
  );
}

/**
 * A set of countries — flag chips that come off one at a time, a picker that
 * adds, and "any country" when the set is empty. The control behind the
 * `country-set` setting definition (W8).
 *
 * Read-only draws the chips without their remove buttons and no picker at all:
 * a disabled picker under a read-only rail would promise an edit the screen
 * cannot make.
 */
export const CountrySetField = forwardRef<HTMLButtonElement, CountrySetFieldProps>(
  function CountrySetField(
    {
      id,
      className,
      size,
      placeholder,
      searchPlaceholder,
      emptyLabel,
      isDisabled = false,
      isReadOnly = false,
      isInvalid = false,
      onBlur,
      'aria-describedby': describedBy,
      'aria-labelledby': labelledBy,
      value,
      onValueChange,
    },
    ref,
  ) {
    const { t } = useTranslation('uiKit');
    const logic = useCountrySetFieldLogic({ value, onValueChange, isDisabled, isReadOnly });

    return (
      <div className={cn('flex flex-col gap-sm', className)}>
        {logic.isEmpty ? (
          <p className={COUNTRY_SET_ANY_CLASSES}>
            <Globe aria-hidden className="h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-col gap-2xs">
              <span className="text-body text-foreground">{t('countrySetField.any')}</span>
              <span>{t('countrySetField.anyHint')}</span>
            </span>
          </p>
        ) : (
          <ul className={SELECTION_CHIP_RAIL_CLASSES} aria-label={t('countrySetField.chosen')}>
            {logic.chips.map((chip) => (
              <li key={chip.code} className="flex min-w-0">
                <span className={SELECTION_CHIP_CLASSES}>
                  <span aria-hidden className="shrink-0">
                    {chip.flag}
                  </span>
                  <span className="truncate">{chip.label}</span>
                  {logic.isEditable ? (
                    <button
                      type="button"
                      aria-label={t('countrySetField.remove', { label: chip.label })}
                      onClick={() => {
                        void logic.remove(chip.code);
                      }}
                      className={COUNTRY_SET_CHIP_REMOVE_CLASSES}
                    >
                      <X aria-hidden className="h-3 w-3" />
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isReadOnly ? null : (
          <MultiSelect
            ref={ref}
            id={id}
            size={size}
            value={value}
            onValueChange={logic.onPicked}
            options={logic.options}
            hasChips={false}
            hasSelectAll={false}
            isClearable={false}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            placeholder={placeholder ?? t('countrySetField.add')}
            searchPlaceholder={searchPlaceholder ?? logic.searchPlaceholder}
            emptyLabel={emptyLabel ?? logic.emptyLabel}
            renderOption={renderCountryOption}
            onBlur={onBlur}
            aria-describedby={describedBy}
            aria-labelledby={labelledBy}
          />
        )}
      </div>
    );
  },
);
